import axios from 'axios';
import { nanoid } from 'nanoid';

import { IParticipant, ParticipantState } from '../interfaces/participants';
import {
  IAddParticipantAvatarData,
  IDeleteParticipantAvatarData,
  IProfile,
  IProfileState,
  IConfigs,
  IUpdateParticipantData,
  IUpdateProfileData,
  IWsConnection,
} from '../interfaces/profile';
import { IParticipantEvents } from '../interfaces/event';
import { IAvatarLimited, IUserAvatar } from '@interfaces/avatar';
import { getCurrentWorldId } from '@helpers/currentWorld';
import { env } from '../env';

const { REACT_APP_API_URL } = env;

export default class ParticipantsService {
  static getParticipantRoom(participantId: string): Promise<{
    participantId: string;
    roomId: string;
  }> {
    return axios
      .get(`${REACT_APP_API_URL}api/v2/participants/${participantId}`)
      .then((res) => res.data);
  }

  static getEmptyParticipant(participantId: string): IParticipant {
    return {
      participantId,
      streamChatMemberId: participantId.replaceAll(/\|/gi, '_'),
      key: nanoid(10),
      email: '',
      name: '',
      firstName: '',
      lastName: '',
      fullName: '',
      avatarString: '',
      avatarImage: '',
      speaking: false,
      eventId: null,
      gameSessionId: null,
      isSeatRegion: false,
      roomId: null,
      region: null,
      isSpeaker: false,
      regions: [],
      state: ParticipantState.Loading,
      customAvatarUrl: '',
    };
  }

  static getConfigs(): Promise<IConfigs> {
    return axios
      .get(`${REACT_APP_API_URL}api/v1/profile/configs`)
      .then((res) => res.data);
  }

  static getWsConnections(): Promise<IWsConnection[]> {
    return axios
      .get(`${REACT_APP_API_URL}api/v1/profile/ws-connections`)
      .then((res) => res.data);
  }

  static async addParticipantAvatarToList(
    data: IAddParticipantAvatarData,
  ): Promise<IUserAvatar[]> {
    const response = await axios.post(
      `${REACT_APP_API_URL}api/v1/profile/avatars`,
      data,
    );

    return response.data;
  }

  static async getParticipantAvatars(): Promise<IUserAvatar[]> {
    return axios
      .get(`${REACT_APP_API_URL}api/v1/profile/avatars`)
      .then((res) => res.data);
  }

  static async deleteParticipantAvatarFromList(
    data: IDeleteParticipantAvatarData,
  ): Promise<IUserAvatar[]> {
    const response = await axios.delete(
      `${REACT_APP_API_URL}api/v1/profile/avatars`,
      { data },
    );

    return response.data;
  }

  static getCurrentParticipantProfile(): Promise<IProfile> {
    return axios
      .get(`${REACT_APP_API_URL}api/v1/profile`)
      .then((res) => res.data);
  }

  static updateCurrentParticipantProfile(
    data: IUpdateProfileData,
  ): Promise<IProfile> {
    return axios
      .patch(`${REACT_APP_API_URL}api/v1/profile`, data)
      .then((res) => res.data);
  }

  static getParticipantAvaturnAvatars(): Promise<IAvatarLimited[]> {
    return axios
      .get(`${REACT_APP_API_URL}api/v1/profile/avaturn/avatars`)
      .then((res) => res.data);
  }

  static async updateCurrentParticipantAvatar(
    data: IUpdateParticipantData,
  ): Promise<IProfile> {
    const response = await axios.patch(
      `${REACT_APP_API_URL}api/v1/profile/avatar`,
      data,
    );

    return response.data;
  }

  static updateCurrentParticipantProfileTC(
    data: IUpdateParticipantData,
  ): Promise<IProfile> {
    return axios.patch(`${REACT_APP_API_URL}api/v1/profile/tcs`, data);
  }

  static updateCurrentParticipantProfileState(
    data: IProfileState,
  ): Promise<IProfile> {
    return axios.patch(`${REACT_APP_API_URL}api/v1/profile/state`, data);
  }

  /**
   * Loads the experiences the signed-in participant may enter, scoped to the
   * project the current domain serves (MV-1930) — a customer domain must not
   * list another customer's experiences.
   *
   * @returns The participant's experiences for this project.
   */
  static getCurrentParticipantEvents(): Promise<IParticipantEvents> {
    const worldId = getCurrentWorldId();

    return axios
      .get(`${REACT_APP_API_URL}api/v1/profile/events`, {
        // Omitted rather than sent empty when the project is not known: an
        // empty `worldId` would match nothing, whereas an absent one lets the
        // backend resolve the project from the `Origin` header.
        params: worldId ? { worldId } : undefined,
      })
      .then((res) => res.data);
  }

  static generateScreenshotUrls(fileName: string) {
    return axios
      .post(`${REACT_APP_API_URL}api/v1/profile/screenshot-urls`, {
        fileName,
      })
      .then((res) => res.data);
  }
}
